

import { gql, useApolloClient, useMutation } from "@apollo/client";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { Button } from "../../components/button";
import { FormError } from "../../components/form-error";
import { createPodcast, createPodcastVariables } from "../../__generated__/createPodcast";
import { MY_PODCASTS_QUERY } from "./my-podcasts";

const CREATE_PODCAST_MUTATION = gql`
  mutation createPodcast($input: CreatePodcastInput!) {
    createPodcast(input: $input) {
      error
      ok
      id
    }
  }
`;

interface IFormProps {
    title: string;
    rating: number;
    categoryName: string;
    file: FileList;
    description: string;
}

export const CreatePodcast = () => {
    const client = useApolloClient();
    const history = useHistory();
    const [imageUrl, setImageUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const { register, getValues, formState, handleSubmit } = useForm<IFormProps>({
        mode: "onChange",
    });

    const onCompleted = (data: createPodcast) => {
        const {
            createPodcast: { ok, id },
        } = data;
        if (ok) {
            const { title, categoryName, rating, description } = getValues();
            setUploading(false);
            const queryResult = client.readQuery({ query: MY_PODCASTS_QUERY });

            client.writeQuery({
                query: MY_PODCASTS_QUERY,
                data: {
                    myPodcasts: {
                        ...queryResult.myPodcasts,
                        podcasts: [
                            {
                                rating: Number(rating),
                                category: {
                                    name: categoryName,
                                    __typename: "Category",
                                },
                                coverImg: imageUrl,
                                description,
                                id,
                                title,
                                __typename: "Podcast",
                            },
                            ...queryResult.myPodcasts.podcasts,
                        ],
                    },
                },
            });
            history.push("/");
        }
    };

    const [createPodcastMutation, { loading, data }] = useMutation<
        createPodcast,
        createPodcastVariables
    >(CREATE_PODCAST_MUTATION, {
        onCompleted,
    });

    const onSubmit = async () => {
        try {
            setUploading(true);
            const { file, title, categoryName, rating, description } = getValues();
            const actualFile = file[0];
            const formBody = new FormData();
            formBody.append("file", actualFile);
            const { url: coverImg } = await (
                await fetch("https://kudok.herokuapp.com/uploads/", {
                    method: "POST",
                    body: formBody,
                })
            ).json();
            setImageUrl(coverImg);
            createPodcastMutation({
                variables: {
                    input: {
                        title,
                        categoryName,
                        description,
                        rating: Number(rating),
                        coverImg,
                    },
                },
            });
        } catch (e) { }
    };

    return (
        <div className="max-w-screen-2xl mx-auto flex flex-col items-center pt-52 pb-96">
            <Helmet>
                <title>Create Podcast | KUDOK</title>
            </Helmet>
            <h1 className="font-semibold text-2xl mb-3">Create Podcast</h1>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="grid max-w-screen-sm gap-3 mt-5 w-full mb-5 text-black"
            >
                <input
                    className=" focus:ring-2  focus:ring-blue-600 focus:ring-opacity-90 focus:outline-none py-3 px-5 rounded-lg"
                    type="text"
                    placeholder="Title"
                    {...register("title", {
                        required: "Title is required",
                    })}
                />
                <input
                    className=" focus:ring-2  focus:ring-blue-600 focus:ring-opacity-90 focus:outline-none py-3 px-5 rounded-lg"
                    type="number"
                    placeholder="Rating"
                    {...register("rating", {
                        required: "Rating is required",
                        min: 1,
                        max: 5,
                    })}
                />
                <input
                    className=" focus:ring-2  focus:ring-blue-600 focus:ring-opacity-90 focus:outline-none py-3 px-5 rounded-lg"
                    type="text"
                    placeholder="Category Name"
                    {...register("categoryName", {
                        required: "Category Name is required",
                    })}
                />
                <input
                    className=" focus:ring-2  focus:ring-blue-600 focus:ring-opacity-90 focus:outline-none py-3 px-5 rounded-lg"
                    type="text"
                    placeholder="Description"
                    {...register("description", {
                        required: "Description is required",
                    })}
                />
                <div>
                    <input
                        type="file"
                        accept="image/*"
                        className="w-full text-gray-700 px-3 py-2 border rounded text-white"
                        {...register("file", {
                            required: true,
                        })}
                    />
                </div>
                <Button
                    loading={uploading}
                    canClick={formState.isValid}
                    actionText="Create Podcast"
                />
                {data?.createPodcast?.error && (
                    <FormError errorMessage={data.createPodcast.error} />
                )}
            </form>
        </div>
    );
};